import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';

interface DatasetFile {
  name: string;
  size_mb: number;
}

interface Dataset {
  id: string;
  exchange: string;
  category: string;
  description: string;
  timeframes: string[];
  pair_count: number;
  total_size_mb: number;
  last_refresh: string | null;
  files: Record<string, DatasetFile>;
  mount_path: string;
  sdk_snippet: string;
  pandas_snippet: string;
}

interface CatalogResponse {
  datasets: Dataset[];
  total_datasets: number;
  total_size_mb: number;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-700/80 hover:bg-gray-600 text-gray-400 hover:text-gray-200 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <svg className="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
    </button>
  );
}

function DatasetCard({ dataset }: { dataset: Dataset }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2.5 mb-1.5">
              <h3 className="text-lg font-semibold text-white capitalize">{dataset.exchange}</h3>
              <span className="px-2 py-0.5 text-[11px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-full">
                {dataset.category}
              </span>
            </div>
            <p className="text-sm text-gray-400">{dataset.description}</p>
          </div>
        </div>

        {/* Timeframe pills */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {dataset.timeframes.map(tf => (
            <span
              key={tf}
              className="px-2 py-0.5 text-xs font-mono bg-gray-800 text-gray-300 rounded border border-gray-700"
            >
              {tf}
            </span>
          ))}
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 text-sm text-gray-400 mb-4">
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>{dataset.pair_count} pairs</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7" />
            </svg>
            <span>{dataset.total_size_mb >= 1024 ? `${(dataset.total_size_mb / 1024).toFixed(1)} GB` : `${dataset.total_size_mb} MB`}</span>
          </div>
          {dataset.last_refresh && (
            <div className="flex items-center gap-1.5">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Updated {new Date(dataset.last_refresh).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        {/* Availability badge */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
            <span className="text-xs text-green-400 font-medium">Available on all GPU instances</span>
          </div>
          <code className="text-[11px] text-gray-500 font-mono">{dataset.mount_path}</code>
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          {expanded ? 'Hide' : 'Show'} usage examples
        </button>
      </div>

      {/* Expanded usage section */}
      {expanded && (
        <div className="border-t border-gray-800 px-6 py-5 space-y-4 bg-gray-900/50">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">PropAlgos SDK</span>
            </div>
            <div className="relative">
              <pre className="bg-gray-950 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-x-auto border border-gray-800">{dataset.sdk_snippet}</pre>
              <CopyButton text={dataset.sdk_snippet} />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Pandas (direct)</span>
            </div>
            <div className="relative">
              <pre className="bg-gray-950 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-x-auto border border-gray-800">{dataset.pandas_snippet}</pre>
              <CopyButton text={dataset.pandas_snippet} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DataPage() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCatalog = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/datasets/catalog`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        setCatalog(await response.json());
      } else {
        setError('Failed to load datasets');
      }
    } catch {
      setError('Failed to connect to API');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-1">Data Catalog</h1>
          <p className="text-gray-400">
            Pre-loaded datasets available on every GPU instance via FUSE mount.
          </p>
        </div>

        {/* Stats bar */}
        {catalog && catalog.total_datasets > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-white">{catalog.total_datasets}</div>
              <div className="text-xs text-gray-500 mt-1">Datasets</div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="text-2xl font-bold text-white">
                {catalog.total_size_mb >= 1024
                  ? `${(catalog.total_size_mb / 1024).toFixed(1)} GB`
                  : `${catalog.total_size_mb} MB`}
              </div>
              <div className="text-xs text-gray-500 mt-1">Total Size</div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="text-sm font-mono text-white mt-1">/home/propalgos/workspace/datasets/</div>
              <div className="text-xs text-gray-500 mt-1">Mount Path</div>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-gray-700 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Dataset cards */}
        {!loading && catalog && catalog.datasets.length > 0 && (
          <div className="space-y-4">
            {catalog.datasets.map(ds => (
              <DatasetCard key={ds.id} dataset={ds} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && catalog && catalog.datasets.length === 0 && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-12 text-center">
            <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">No datasets available yet</h2>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              The data pipeline is being populated. Datasets will appear here once the initial refresh completes.
            </p>
            <button
              onClick={() => navigate('/dashboard/storage')}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm font-medium transition-colors border border-gray-700"
            >
              Go to Storage
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
